/**
 * ProposalEngagementPanel - Resumo de engajamento do link público
 *
 * Deriva no cliente, a partir dos eventos já retornados por
 * `get_proposal_details`, os números de engajamento da proposta:
 *   - visualizações distintas (agrupadas por session_id);
 *   - tempo total e tempo médio de permanência na página;
 *   - PDFs gerados e downloads feitos pelo cliente.
 *
 * Nenhum cálculo adicional é empurrado para o banco.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-11 v1.0.0 - Criação (refatoração do modal de detalhes da
 *            proposta — coluna lateral de engajamento)
 */

import React from 'react';

/** Evento de tracking (mesmo shape usado pela timeline) */
export interface ProposalEngagementEvent {
  event_type: string;
  session_id?: string | null;
  created_at: string;
  metadata?: Record<string, unknown> | null;
}

interface ProposalEngagementPanelProps {
  events: ProposalEngagementEvent[];
}

/** Tipos de evento que representam uma visualização do link público */
const VIEW_EVENTS = ['viewed', 'opened', 'public_viewed'];

/** Converte segundos em rótulo curto legível (18s, 2min 05s, 1h 04min) */
function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  if (seconds === 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return `${minutes}min${rest ? ` ${String(rest).padStart(2, '0')}s` : ''}`;
  const hours = Math.floor(minutes / 60);
  const restMin = minutes % 60;
  return `${hours}h${restMin ? ` ${String(restMin).padStart(2, '0')}min` : ''}`;
}

/** Linha de métrica (rótulo à esquerda, valor à direita) */
function MetricRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs font-semibold text-foreground">{value}</span>
    </div>
  );
}

/**
 * Painel compacto com os indicadores de engajamento da proposta.
 */
export function ProposalEngagementPanel({ events }: ProposalEngagementPanelProps) {
  const metrics = React.useMemo(() => {
    const list = events ?? [];

    // Visualizações distintas por sessão (fallback: timestamp quando não há sessão)
    const sessions = new Set<string>();
    let totalSeconds = 0;

    list.forEach((event) => {
      if (VIEW_EVENTS.includes(event.event_type)) {
        sessions.add(event.session_id || `anon-${event.created_at}`);
      }
      totalSeconds += Number((event.metadata as any)?.duration_seconds ?? 0) || 0;
    });

    const pdfGenerated = list.filter((e) => e.event_type === 'pdf_generated').length;
    const clientDownloads = list.filter((e) => e.event_type === 'downloaded_pdf').length;
    const views = sessions.size;

    return {
      views,
      totalSeconds,
      averageSeconds: views > 0 ? totalSeconds / views : 0,
      pdfGenerated,
      clientDownloads,
    };
  }, [events]);

  return (
    <div className="space-y-3">
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Engajamento
      </h3>
      <div className="rounded-xl border border-border bg-card p-3 space-y-2.5">
        <MetricRow label="Visualizações" value={`${metrics.views}x`} />
        <MetricRow label="Tempo total" value={formatDuration(metrics.totalSeconds)} />
        <MetricRow label="Tempo médio" value={formatDuration(metrics.averageSeconds)} />
        <MetricRow label="PDFs gerados" value={metrics.pdfGenerated} />
        <MetricRow label="Downloads do cliente" value={metrics.clientDownloads} />
      </div>
    </div>
  );
}
